<template>
    <div class="settings-emails-templates-form">
        <CompassFormItem
            :label="$t.__('email.SettingsEmailsTemplatesForm.label_template_name')"
            :error="errorLabelTemplateName"
            required
            :requiredLabel="$t.__('common.labels.required')"
        >
            <CompassInput
                v-model="templateName"
                :error="!!errorLabelTemplateName"
                :placeholder="$t.__('email.SettingsEmailsTemplatesForm.placeholder_template_name')"
                @input="checkLimitTemplateName"
                @focus="checkLimitTemplateName"
                @keypress="checkTypingTemplateName"
            />
        </CompassFormItem>
        <RichText
            ref="refSettingsEmailsTemplatesFormRichTextSubject"
            v-model="templateSubject"
            refRichText="refSettingsEmailsTemplatesFormRichTextSubject"
            :formitem="formItemSujectRichText"
            :placeholder="$t.__('email.SettingsEmailsTemplatesForm.placeholder_subject')"
            :options="optionsSubjectRichText"
            disableEnter
            class="settings-emails-templates-form__richtext-subject"
        />
        <CompassFormItem
            :label="formItemMessageRichText.label"
            :error="formItemMessageRichText.error"
            required
            :requiredLabel="$t.__('common.labels.required')"
        >
            <div class="settings-emails-templates-form__message" :class="messageClass">
                <RichText
                    ref="refSettingsEmailsTemplatesFormRichTextMessage"
                    v-model="templateMessage"
                    class="settings-emails-templates-form__richtext-message"
                    refRichText="refSettingsEmailsTemplatesFormRichTextMessage"
                    :placeholder="
                        $t.__('email.SettingsEmailsTemplatesForm.placeholder_message_email')
                    "
                    :options="optionsMessageRichText"
                    typeRichText="email"
                    :showRichTextMenu="false"
                    :hash="getHashMessage"
                    :showAIComponents="isSellsyAIEnabled"
                    @focus="setIsEditorFocused(true)"
                    @blur="setIsEditorFocused(false)"
                />
                <div v-if="attachments.length" class="settings-emails-templates-form__attachments">
                    <EmailAttachments
                        v-for="(item, index) in attachments"
                        :key="index"
                        :attachment="item"
                        @removeItem="removeAttachment"
                    />
                </div>
                <RichTextMenu
                    v-if="richTextEditor"
                    :editorMenu="richTextEditor"
                    :options="optionsMessageRichText"
                    typeRichText="email"
                    :hash="getHashMessage"
                    refRichText="refSettingsEmailsTemplatesFormRichTextMessage"
                    attachmentEndpoint="/email/templates/files"
                    class="email-richtext-menu"
                />
            </div>
        </CompassFormItem>
    </div>
</template>

<script>
import generateHash from 'Helpers/hashGenerator.js';
import RichText from 'vcomponents/richtext/RichText.vue';
import RichTextMenu from 'vcomponents/richtext/RichTextMenu.vue';
import EmailAttachments from 'vcomponents/email/emailPanels/EmailAttachments.vue';
import Feature from 'Services/Feature';
import StoreAI from 'stores/ai';
import RichTextAttachmentsMixin from 'mixins/RichTextAttachmentsMixin';

export default {
    components: {
        RichText,
        RichTextMenu,
        EmailAttachments,
    },
    mixins: [RichTextAttachmentsMixin],
    props: {
        mode: {
            type: String,
            required: true,
        },
        tags: {
            type: Array,
            required: true,
        },
        template: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            attachments: [],
            richTextEditor: null,
            isEditorFocused: false,
            templateName: '',
            templateSubject: '',
            templateMessage: '',
            optionsSubjectRichText: ['CustomTagsAndFields'],
            optionsMessageRichText: [
                'BlockquoteTiptap',
                'SellsyAI',
                'Heading',
                'Bold',
                'Italic',
                'Underline',
                'Color',
                'Highlight',
                'BulletList',
                'OrderedList',
                'TextAlign',
                'Divider',
                'Link',
                'ImageResizable',
                'Attachment',
                'Divider',
                'CustomTagsAndFields',
                'CustomTagsAI',
            ],
            errorLabelTemplateName: '',
            errorLabelSubject: '',
            errorLabelMessage: '',
        };
    },
    computed: {
        messageClass() {
            return {
                'settings-emails-templates-form__message--is-focused': this.isEditorFocused,
            };
        },
        getHashMessage() {
            return generateHash();
        },
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
        formItemSujectRichText() {
            return {
                label: this.$t.__('common.labels.suject'),
                error: this.errorLabelSubject,
                required: true,
                requiredLabel: this.$t.__('common.labels.required'),
            };
        },
        formItemMessageRichText() {
            return {
                label: this.$t.__('email.SettingsEmailsTemplatesForm.label_message_email'),
                error: this.errorLabelMessage,
                required: true,
            };
        },
    },
    created() {
        if (!this.$store.state[`ai-${this.getHashMessage}`] && this.isSellsyAIEnabled) {
            this.$store.registerModule(`ai-${this.getHashMessage}`, StoreAI);
        }
        if ((this.mode === 'edit' || this.mode === 'duplicate') && this.template) {
            this.templateName = this.template.name;
            this.templateSubject = this.template.subject;
            this.templateMessage = this.template.message;
            this.attachments = this.template.attachments || [];
        }
        EventBus.on(`richText-${this.getHashMessage}:loadingAttachments`, (fileInfo) => {
            this.attachments.push(fileInfo);
        });
        EventBus.on(`richText-${this.getHashMessage}:addAttachment`, (file) => {
            this.updateAttachmentList(file);
        });

        EventBus.on('settingsEmailsTemplates:save', this.handleSave);
    },
    mounted() {
        this.richTextEditor = this.$refs.refSettingsEmailsTemplatesFormRichTextMessage.editor;
    },
    beforeUnmount() {
        EventBus.off(`richText-${this.getHashMessage}:addAttachment`);
        EventBus.off(`richText-${this.getHashMessage}:loadingAttachments`);
        this.$store.dispatch(`ai-${this.getHashMessage}/setTemplate`, this.template);
    },
    unmounted() {
        if (this.$store.state[`ai-${this.getHashMessage}`] && this.isSellsyAIEnabled) {
            this.$store.unregisterModule(`ai-${this.getHashMessage}`, StoreAI);
        }
    },
    methods: {
        setIsEditorFocused(value) {
            this.isEditorFocused = value;
            this.$emit('is-editor-focused', value);
        },
        checkForm() {
            let validForm = true;
            if (!this.templateName) {
                validForm = false;
                this.errorLabelTemplateName = this.$t.__(
                    'email.SettingsEmailsTemplatesForm.error_label_template_name',
                );
            } else {
                this.errorLabelTemplateName = '';
            }
            if (!this.templateSubject) {
                validForm = false;
                this.errorLabelSubject = this.$t.__(
                    'email.SettingsEmailsTemplatesForm.error_label_subject',
                );
            } else {
                // Remove all html tags from the subject
                const parseHTMLTemplateSubject = new DOMParser().parseFromString(
                    this.templateSubject,
                    'text/html',
                );
                const spans = parseHTMLTemplateSubject.querySelectorAll('span');
                for (const span of spans) {
                    const tagValue = span.getAttribute('tag');
                    if (!tagValue || tagValue.trim() === '') {
                        span.replaceWith(...span.childNodes);
                    }
                }
                let cleanedHTML = parseHTMLTemplateSubject.body.innerHTML.trim();
                cleanedHTML = cleanedHTML.replace(/<\/?p[^>]*>/g, '');
                this.templateSubject = cleanedHTML;

                this.errorLabelSubject = '';
            }
            if (!this.templateMessage) {
                validForm = false;
                this.errorLabelMessage = this.$t.__(
                    'email.SettingsEmailsTemplatesForm.error_label_message',
                );
            } else {
                this.errorLabelMessage = '';
            }
            return validForm;
        },
        replaceSpanWithTag(content) {
            if (!this.tags) {
                return content;
            }

            const regex =
                /<span [^>]*tag="([^"]+)"[^>]*class="[^"]*custom-tags-and-fields[^"]*"[^>]*>[^<]+<\/span>/g;

            return content.replace(regex, (match, tag) => {
                const tagObj = this.tags.find((t) => t.key === tag);
                return tagObj ? tagObj.key : match;
            });
        },
        replaceSpanWithTagAI(message) {
            const div = document.createElement('div');
            div.innerHTML = message;
            const spans = div.querySelectorAll('span[type="ai"]');
            for (const span of spans) {
                const hash = span.getAttribute('hash') || '';
                const label = span.getAttribute('label') || '';
                const newContent = `%ai{id="${hash}" title="${label}"}ai%`;
                span.outerHTML = newContent;
            }
            const finalMessage = div.innerHTML;
            return finalMessage;
        },
        getRelatedAI(message) {
            const div = document.createElement('div');
            div.innerHTML = message;
            const spans = div.querySelectorAll('span[type="ai"]');
            const relatedAI = [];
            for (const span of spans) {
                const hash = span.getAttribute('hash') || '';
                const label = span.getAttribute('label') || '';
                let prompt = span.getAttribute('prompt') || '';
                if (prompt) {
                    prompt = this.replaceSpanWithTag(prompt);
                }
                relatedAI.push({
                    id: hash,
                    title: label,
                    prompt,
                });
            }
            if (relatedAI.length === 0) {
                return [];
            }
            return relatedAI;
        },

        handleSave() {
            if (this.checkForm()) {
                const tmpSuject = this.replaceSpanWithTag(this.templateSubject);
                let tmpMessage = this.replaceSpanWithTag(this.templateMessage);
                tmpMessage = this.replaceSpanWithTagAI(tmpMessage);

                const template = {
                    name: this.templateName,
                    subject: tmpSuject,
                    message: tmpMessage,
                    related_ai: this.getRelatedAI(this.templateMessage),
                    attachments: this.getAttachmentsIds(),
                };
                this.$emit('save-email-template', template);
            }
        },
        getAttachmentsIds() {
            return (
                this.attachments.map((attachment) => attachment.content_id || attachment.id) || []
            );
        },
        checkLimitTemplateName(event) {
            if (event.target.value.length > 254) {
                this.templateName = this.templateName.substring(0, 255);
                this.errorLabelTemplateName = this.$t.__(
                    'email.SettingsEmailsTemplatesForm.error_limit_characters',
                );
            } else {
                this.errorLabelTemplateName = '';
            }
        },
        checkTypingTemplateName(event) {
            if (event.target.value.length >= 255) {
                event.preventDefault();
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.settings-emails-templates-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    :deep(.compass-form-item__input) {
        position: relative;
    }

    &__richtext {
        &-subject {
            :deep(.tiptap.ProseMirror) {
                min-height: 34px;
                max-height: 73px;
            }
        }
        &-message {
            :deep(.tiptap.ProseMirror) {
                border: 0;
            }
        }
    }

    &__message {
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0);

        &--is-focused {
            border-color: var(--colors-common-border-focus);
        }
    }

    &__attachments {
        display: flex;
        flex-direction: column;
        padding: 0 var(--spacing-1);
    }
}
</style>
